import { isShotgunRound, normalizeShotgunOrder } from '@moment-golf/shared';
import { formatHoleSlotLabel } from '../Functions/shotgunTeeSheetUtils';

// ─── Card Element Types ───────────────────────────────────────────────────────

export type CardElementType = 'text' | 'image' | 'qrcode';

export type TextAlign = 'left' | 'center' | 'right';

export interface CardElement {
    id: string;
    type: CardElementType;
    /** Position as percentage of card width/height (0–100) */
    x: number;
    y: number;
    /** Size as percentage of card width/height (0–100) */
    width: number;
    height: number;
    /** Static text string or a binding token like "{{playerName}}" */
    content?: string;
    fontSize?: number;
    fontWeight?: 'normal' | 'bold';
    color?: string;
    textAlign?: TextAlign;
    /** Base-64 data URL for image elements */
    imageDataUrl?: string;
}

// ─── Card Template ────────────────────────────────────────────────────────────

export interface CardTemplate {
    backgroundColor: string;
    elements: CardElement[];
}

export const DEFAULT_CARD_TEMPLATE: CardTemplate = {
    backgroundColor: '#ffffff',
    elements: [],
};

// ─── Data Binding Tokens ─────────────────────────────────────────────────────

/**
 * Tokens that can be embedded in a text element's `content` field.
 * At render time they are replaced with the matching registration field value.
 */
export const BINDING_TOKENS = [
    { token: '{{playerName}}',  label: 'Player Name' },
    { token: '{{teamName}}',    label: 'Team Name' },
    { token: '{{scoreCode}}',   label: 'Score Code' },
    { token: '{{teeTime}}',     label: 'Tee Time' },
    { token: '{{startingHole}}', label: 'Starting Hole' },
    { token: '{{division}}',    label: 'Division' },
    { token: '{{flight}}',      label: 'Flight' },
    { token: '{{groupNumber}}', label: 'Group #' },
] as const;

export type BindingToken = (typeof BINDING_TOKENS)[number]['token'];

// ─── Designer canvas constants ────────────────────────────────────────────────
// These are the canonical canvas dimensions used in the template designer.
// All other render targets (grid cards, PDF) scale font sizes relative to these.
export const DESIGNER_CANVAS_W = 616;
export const DESIGNER_CANVAS_H = 440; // Math.round(616 * 5 / 7)

// ─── QR URL builder ───────────────────────────────────────────────────────────

export function buildLiveScoringUrl(eventId: string, scoreCode: string): string {
    const base = process.env.REACT_APP_LIVE_SCORING_URL ?? '';
    return `${base}?scoreCode=${encodeURIComponent(scoreCode)}&eventId=${encodeURIComponent(eventId)}`;
}

// ─── Starting hole resolver ───────────────────────────────────────────────────

export function resolveRegistrationStartingHole(
    reg: Record<string, unknown>,
    roundValue: number,
    eventData?: Record<string, unknown>,
): string {
    const teeTimes = reg.teeTimes as Array<{ startingHole?: number; shotgunOrder?: string | null }> | undefined;
    const teeTimeSlot = teeTimes?.[roundValue - 1];
    const slotHole = teeTimeSlot?.startingHole;

    const rounds = eventData?.rounds as Array<{ startHole?: number; shotgunStart?: unknown }> | undefined;
    const roundConfig = rounds?.[roundValue - 1];

    if (slotHole != null) {
        if (isShotgunRound(roundConfig)) {
            return formatHoleSlotLabel(slotHole, normalizeShotgunOrder(teeTimeSlot?.shotgunOrder));
        }
        return String(slotHole);
    }

    const roundHole = roundConfig?.startHole;
    if (roundHole != null) return String(roundHole);

    return '—';
}

/**
 * Parse a starting-hole string into a valid hole number (1–18), or null.
 * Strips any trailing A/B suffix (e.g. "5A" → 5, "12B" → 12) so that
 * grid-column highlighting still works for shotgun rounds.
 */
export function parseStartingHoleNumber(value: string): number | null {
    const n = Number(value.replace(/[A-Za-z]+$/, ''));
    if (!Number.isFinite(n) || n < 1 || n > 18) return null;
    return Math.round(n);
}

// ─── Binding resolver ────────────────────────────────────────────────────────

export function resolveBindings(
    content: string,
    reg: Record<string, unknown>,
    roundValue: number,
    eventData?: Record<string, unknown>,
): string {
    const teeTimeRaw = (reg.teeTimes as Array<{ teeTime?: string }> | undefined)?.[roundValue - 1]?.teeTime;
    const teeTimeFormatted = teeTimeRaw
        ? new Date(teeTimeRaw).toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              timeZone: 'UTC',
          })
        : '—';

    const startingHole = resolveRegistrationStartingHole(reg, roundValue, eventData);

    return content
        .replace(/\{\{playerName\}\}/g,  String(reg.playerName  ?? ''))
        .replace(/\{\{teamName\}\}/g,    String(reg.teamName    ?? ''))
        .replace(/\{\{scoreCode\}\}/g,   String(reg.scoreCode   ?? ''))
        .replace(/\{\{teeTime\}\}/g,     teeTimeFormatted)
        .replace(/\{\{startingHole\}\}/g, startingHole)
        .replace(/\{\{division\}\}/g,    String(reg.division    ?? ''))
        .replace(/\{\{flight\}\}/g,      String(reg.flight      ?? ''))
        .replace(/\{\{groupNumber\}\}/g, String(reg.groupNumber ?? ''));
}
